import { getLocalTimeZone, today } from '@internationalized/date'

import type { TParametrosRotinaArquivo } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import { RotinaArquivoBase } from '@/domain/simulador/factories/concrete/horizontal/sacocv/rotina-arquivo-base'
import type {
  TSimulacaoCalculoPrazo,
  TSimulacaoCalculoRotinaArquivo,
  TSimulacaoCalculoValoresTotais,
  TSimulacaoFluxoPagamento,
} from '@/domain/simulador/factories/types/simulacao-calculo'
import { GerenciadorEntradasBase } from '@/domain/simulador/gerenciador-entradas-base'
import { GerenciadorIntermediariasBase } from '@/domain/simulador/gerenciador-intermediarias-base'
import { GerenciadorMensaisBase } from '@/domain/simulador/gerenciador-mensais-base'
import { GerenciadorPosConclusaoBase } from '@/domain/simulador/gerenciador-pos-conclusao-base'
import { GerenciadorSaldoBase } from '@/domain/simulador/gerenciador-saldo-base'
import type { TParametrosDinamicosSimulacaoSimulador, TSimulacaoSchema } from '@/domain/simulador/types/simulacao.type'
import { CalculadorFluxoDePagamentoBase } from '@/features/simulador/brasil/domain/calculador-fluxo-pagamento-base'
import { CalculadorPrazoBase } from '@/features/simulador/brasil/domain/calculador-prazo-base'
import type { TRotinaArquivo } from '@/features/simulador/brasil/http/create-simulacao.service'
import type { TGetSimulacaoDuplicarResponse, TTipoAmortizacaoEnum } from '@/types/simulacao'
import { CalculadoraDeValoresTotaisBase } from '@/utils/calculadora-valores-totais'
import { GruposParcelasApiMapper } from '@/utils/grupos-parcelas-api.mapper'

type TSimulacaoVerticalBase = {
  condicaoDePrecoId: string
  tabelaDePrecoId: string
  bloco: { idExterno: string; nome: string }
  produto: { idExterno: string; nome: string; metragem?: string }
  dataSaldoCondicaoPreco: string
  dataPrevisaoDeEntregaDoBloco: string
  tipoDeAmortizacao: TTipoAmortizacaoEnum | null
}

export class SimulacaoVertical {
  private readonly dataSimulacao: string = today(getLocalTimeZone()).toString()

  readonly entradas = new GerenciadorEntradasBase()
  readonly mensais = new GerenciadorMensaisBase()
  readonly intermediarias = new GerenciadorIntermediariasBase()
  readonly posConclusao = new GerenciadorPosConclusaoBase()
  readonly saldo = new GerenciadorSaldoBase()

  private readonly criarGruposParcela = (parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador) => {
    const { condicaoDePreco, tabelaDePreco } = parametrosDinamicos

    const datasSinais = [
      condicaoDePreco.dataSinal1,
      condicaoDePreco.dataSinal2,
      condicaoDePreco.dataSinal3,
      condicaoDePreco.dataSinal4,
    ].filter((dataSinal): dataSinal is string => Boolean(dataSinal))

    const validacaoPosConclusao = this.posConclusao.deveGerarPosConclusao(
      condicaoDePreco.previsaoDeEntregaDoBloco,
      this.dataSimulacao,
    )

    return {
      entradas: this.entradas.criarGrupoInicial(datasSinais, tabelaDePreco.valorBaseSinal, {
        data: condicaoDePreco.dataAto,
        valor: tabelaDePreco.valorBaseAto,
      }),
      mensais: this.mensais.criarGrupoInicial(
        condicaoDePreco.dataPrimeiraMensal,
        condicaoDePreco.dataUltimaMensal,
        tabelaDePreco.valorBaseMensais,
      ),
      intermediarias: this.intermediarias.criarGrupoInicial(
        condicaoDePreco.dataDasIntermediarias,
        tabelaDePreco.valorBaseIntermediarias,
      ),
      saldo: this.saldo.criar(condicaoDePreco.dataSaldo, tabelaDePreco.valorBaseSaldo),
      posConclusao: validacaoPosConclusao.sucesso
        ? this.posConclusao.criarGrupo(
            tabelaDePreco.valorBaseMensais,
            condicaoDePreco.dataSaldo,
            condicaoDePreco.previsaoDeEntregaDoBloco,
            this.dataSimulacao,
          )
        : undefined,
    }
  }

  private criarSimulacaoBase(parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoVerticalBase {
    const { condicaoDePreco, tabelaDePreco, bloco, produto } = parametrosDinamicos

    return {
      condicaoDePrecoId: condicaoDePreco.id,
      tabelaDePrecoId: tabelaDePreco.id,
      bloco: { idExterno: String(bloco.idExterno), nome: bloco.nome },
      produto: {
        ...produto,
        idExterno: String(produto.idExterno),
      },
      dataSaldoCondicaoPreco: condicaoDePreco.dataSaldo,
      dataPrevisaoDeEntregaDoBloco: condicaoDePreco.previsaoDeEntregaDoBloco,
      tipoDeAmortizacao: condicaoDePreco.tipoDeAmortizacao,
    }
  }

  criar = (parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador): TSimulacaoSchema<'Vertical'> => {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)

    return {
      id: crypto.randomUUID(),
      dataSimulacao: this.dataSimulacao,
      ...simulacaoBase,
      ...grupos,
      situacao: { status: 'EmAndamento' as const, dados: undefined },
    }
  }

  duplicar = (
    simulacao: TGetSimulacaoDuplicarResponse,
    parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador,
  ): TSimulacaoSchema<'Vertical'> => {
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)
    const { entradas, intermediarias, mensais, posConclusao, saldo } = new GruposParcelasApiMapper(
      simulacao,
    ).buildGruposParcelas()

    return {
      id: crypto.randomUUID(),
      simulacaoAnteriorId: simulacao.id,
      dataSimulacao: this.dataSimulacao,
      ...simulacaoBase,
      entradas,
      mensais,
      intermediarias,
      saldo: saldo!,
      posConclusao,
      situacao: { status: 'EmAndamento' as const, dados: undefined },
    }
  }

  recalcular = (parametrosDinamicos: TParametrosDinamicosSimulacaoSimulador) => {
    const grupos = this.criarGruposParcela(parametrosDinamicos)
    const simulacaoBase = this.criarSimulacaoBase(parametrosDinamicos)

    return {
      ...simulacaoBase,
      ...grupos,
    }
  }

  calcularRotinaArquivo(
    grupos: TSimulacaoCalculoRotinaArquivo,
    parametrosRotinaArquivo: TParametrosRotinaArquivo,
  ): TRotinaArquivo | null {
    const rotinaArquivo = new RotinaArquivoBase(parametrosRotinaArquivo)
    return rotinaArquivo.calcular(grupos)
  }

  calcularValoresTotais(grupos: TSimulacaoCalculoValoresTotais, valorTabela = 0) {
    return new CalculadoraDeValoresTotaisBase(grupos, valorTabela)
  }

  calcularFluxoDePagamento(simulacao: TSimulacaoFluxoPagamento): CalculadorFluxoDePagamentoBase {
    return new CalculadorFluxoDePagamentoBase(
      simulacao.entradas,
      simulacao.mensais,
      simulacao.intermediarias,
      simulacao.saldo,
      simulacao.posConclusao,
    )
  }

  calcularPrazo(simulacao: TSimulacaoCalculoPrazo) {
    return new CalculadorPrazoBase(simulacao)
  }
}
